/// <reference lib="dom"/>
import type { WbaxParam, WbaxPeriodicWave } from "@/types.tsx";

export type WbaxAttrSetter<N> = (node: N, value: any, ctx: BaseAudioContext) => void;

export interface WbaxNodeDef<N extends AudioNode = any, P = any> {
  create(ctx: BaseAudioContext, props: P): N;
  params?: readonly string[];
  attrs?: Record<string, true | WbaxAttrSetter<N>>;
  recreate?: readonly string[];
  input?: boolean;
  external?: boolean;
  source?: (node: N, when: number, props: P) => void;
  dynamicParams?: (props: P) => Record<string, WbaxParam> | undefined;
  paramOf?: (node: N, name: string) => AudioParam | undefined;
}

export const WbaxCommonAttrs = [
  "channelCount",
  "channelCountMode",
  "channelInterpretation",
] as const;

const start = (node: AudioScheduledSourceNode, when: number) => node.start(when);

const setWave: WbaxAttrSetter<OscillatorNode> = (node, wave: WbaxPeriodicWave, ctx) =>
  node.setPeriodicWave(
    ctx.createPeriodicWave(Float32Array.from(wave.real), Float32Array.from(wave.imag), {
      disableNormalization: wave.disableNormalization ?? false,
    }),
  );

const setCurve: WbaxAttrSetter<WaveShaperNode> = (node, curve: ArrayLike<number> | null) => {
  node.curve = curve ? Float32Array.from(curve) : null;
};

const gainDef: WbaxNodeDef<GainNode> = {
  create: (ctx) => ctx.createGain(),
  params: ["gain"],
};

export const WbaxNodes: Record<string, WbaxNodeDef> = {
  gain: gainDef,
  mix: gainDef,
  osc: {
    create: (ctx) => ctx.createOscillator(),
    params: ["frequency", "detune"],
    attrs: { type: true, wave: setWave },
    input: false,
    source: start,
  } satisfies WbaxNodeDef<OscillatorNode>,
  biquad: {
    create: (ctx) => ctx.createBiquadFilter(),
    params: ["frequency", "Q", "gain", "detune"],
    attrs: { type: true },
  } satisfies WbaxNodeDef<BiquadFilterNode>,
  delay: {
    create: (ctx, props: { maxDelay?: number }) => ctx.createDelay(props.maxDelay ?? 1),
    params: ["delayTime"],
    recreate: ["maxDelay"],
  } satisfies WbaxNodeDef<DelayNode>,
  panner: {
    create: (ctx) => ctx.createStereoPanner(),
    params: ["pan"],
  } satisfies WbaxNodeDef<StereoPannerNode>,
  compressor: {
    create: (ctx) => ctx.createDynamicsCompressor(),
    params: ["threshold", "knee", "ratio", "attack", "release"],
  } satisfies WbaxNodeDef<DynamicsCompressorNode>,
  shaper: {
    create: (ctx) => ctx.createWaveShaper(),
    attrs: { curve: setCurve, oversample: true },
  } satisfies WbaxNodeDef<WaveShaperNode>,
  convolver: {
    create: (ctx) => ctx.createConvolver(),
    attrs: { normalize: true, buffer: true },
  } satisfies WbaxNodeDef<ConvolverNode>,
  buffer: {
    create: (ctx, props: { buffer: AudioBuffer | null }) => {
      const node = ctx.createBufferSource();
      node.buffer = props.buffer;
      return node;
    },
    params: ["playbackRate", "detune"],
    attrs: { loop: true, loopStart: true, loopEnd: true },
    recreate: ["buffer"],
    input: false,
    source: (node, when, props: { offset?: number; duration?: number }) =>
      props.duration === undefined
        ? node.start(when, props.offset ?? 0)
        : node.start(when, props.offset ?? 0, props.duration),
  } satisfies WbaxNodeDef<AudioBufferSourceNode>,
  constant: {
    create: (ctx) => ctx.createConstantSource(),
    params: ["offset"],
    input: false,
    source: start,
  } satisfies WbaxNodeDef<ConstantSourceNode>,
  analyser: {
    create: (ctx) => ctx.createAnalyser(),
    attrs: { fftSize: true, smoothingTimeConstant: true, minDecibels: true, maxDecibels: true },
  } satisfies WbaxNodeDef<AnalyserNode>,
  merger: {
    create: (ctx, props: { channels?: number }) => ctx.createChannelMerger(props.channels ?? 2),
    recreate: ["channels"],
  } satisfies WbaxNodeDef<ChannelMergerNode>,
  splitter: {
    create: (ctx, props: { channels?: number }) => ctx.createChannelSplitter(props.channels ?? 2),
    recreate: ["channels"],
  } satisfies WbaxNodeDef<ChannelSplitterNode>,
  iir: {
    create: (ctx, props: { feedforward: number[]; feedback: number[] }) =>
      ctx.createIIRFilter(props.feedforward, props.feedback),
    recreate: ["feedforward", "feedback"],
  } satisfies WbaxNodeDef<IIRFilterNode>,
  worklet: {
    create: (ctx, props: { name: string; options?: AudioWorkletNodeOptions }) =>
      new AudioWorkletNode(ctx, props.name, props.options),
    recreate: ["name", "options"],
    dynamicParams: (props: { params?: Record<string, WbaxParam> }) => props.params,
    paramOf: (node, name) => node.parameters.get(name),
  } satisfies WbaxNodeDef<AudioWorkletNode>,
  external: {
    create: (_ctx, props: { node: AudioNode }) => props.node,
    recreate: ["node"],
    external: true,
  } satisfies WbaxNodeDef<AudioNode>,
};

export function RegisterWbaxNode<N extends AudioNode, P>(tag: string, def: WbaxNodeDef<N, P>) {
  WbaxNodes[tag] = def;
}

export function WbaxNodeAcceptsInput(def: WbaxNodeDef, node: AudioNode) {
  return def.input ?? (def.external ? node.numberOfInputs > 0 : true);
}
